import { Injectable, signal } from '@angular/core';
import {
  ClientContextSnapshot,
  DEFAULT_RESOURCE_FILTERS,
  EligibilityStatus,
  RankedResource,
  ResourceCategory,
  ResourceFilters,
  ResourceSortKey,
  ResourceType,
  ResourcesState,
  applyFilters,
} from './client-resources.model';

// ---------------------------------------------------------------------------
// Deterministic seeding helpers
// ---------------------------------------------------------------------------

function djb2(str: string): number {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 33) ^ str.charCodeAt(i);
  }
  return hash >>> 0;
}

function seedPick<T>(seed: string, options: readonly T[]): T {
  return options[djb2(seed) % options.length];
}

function isoDate(daysAgo: number): string {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  d.setHours(10, 0, 0, 0);
  return d.toISOString();
}

// ---------------------------------------------------------------------------
// Seed data catalogues
// ---------------------------------------------------------------------------

const EMPLOYER_NAMES = [
  'Bright Financial Group',
  'Meridian Corp',
  'Summit Enterprises',
  'Apex Holdings',
] as const;

const REGIONS = [
  'Cook County, IL',
  'Los Angeles County, CA',
  'Harris County, TX',
  'Maricopa County, AZ',
] as const;

const RESOURCE_NAMES: Record<ResourceType, string[]> = {
  Employer: [
    'Employee Assistance Program (EAP)',
    'Emergency Hardship Fund',
    'Tuition Reimbursement Program',
    'Commuter Benefits Plan',
    'Dependent Care FSA',
    'Financial Wellness Coaching',
    'Legal Assistance Benefit',
    'Short-Term Disability Bridge',
  ],
  Community: [
    'Regional Food Bank Network',
    'Community Housing Stabilization Fund',
    'Legal Aid Society',
    'Workforce Development Center',
    'Transportation Assistance Program',
    'Child Care Resource & Referral',
    'Emergency Utility Assistance',
    'Tax Preparation Assistance (VITA)',
    'Healthcare Navigator Program',
    'Debt Counseling Services',
  ],
  Other: [
    'Federal SNAP Benefits',
    'Medicaid Enrollment Support',
    'EITC Outreach Program',
    'HUD Housing Counseling',
    'LIHEAP Energy Assistance',
  ],
};

const CATEGORIES_BY_TYPE: Record<ResourceType, ResourceCategory[]> = {
  Employer:  ['cash_support', 'healthcare', 'childcare', 'education', 'legal', 'transportation'],
  Community: ['housing', 'food', 'debt', 'transportation', 'legal', 'tax', 'healthcare', 'childcare'],
  Other:     ['food', 'healthcare', 'housing', 'tax', 'cash_support'],
};

const WHY_SHOWN_TEMPLATES = [
  'Client is full-time at {employer} with tenure > 6 months and lives in an eligible county.',
  'Client household size qualifies for income-based assistance in {region}.',
  'Client has expressed transportation barriers — this program covers {region}.',
  'Employer {employer} has an active contract with this provider.',
  'High regional success rate for clients with similar profiles in {region}.',
  'Client is eligible based on employment type and household size.',
];

const WHY_NOT_SHOWN_TEMPLATES = [
  'Minimum tenure is 12 months; client currently has 8 months.',
  'Program is limited to part-time employees; client is full-time.',
  'Coverage region does not include {region}.',
  'Income threshold not met based on available data.',
];

const CRITERIA_MET_OPTIONS = [
  'Full-time employment status',
  'Employer contract active',
  'Residency in eligible county',
  'Household size ≥ 2',
  'Tenure > 6 months',
  'Employment type match',
];

const CRITERIA_MISSING_OPTIONS = [
  'Minimum 12-month tenure not yet reached',
  'Income documentation not on file',
  'Dependent verification required',
];

const CRITERIA_UNKNOWN_OPTIONS = [
  'Exact household income not confirmed',
  'Dependent count unverified',
  'Prior program enrollment status unknown',
];

const EVIDENCE_SOURCES = [
  'Employer policy matrix v2.3',
  'Client profile data',
  'Regional eligibility database',
  'Historical success records',
  'Findhelp feed (2024-Q4)',
];

const RANK_REASON_CODES = [
  'eligibility_fit',
  'employer_match',
  'regional_success',
  'need_relevance',
  'data_freshness',
  'historical_effectiveness',
];

// ---------------------------------------------------------------------------
// Seeding
// ---------------------------------------------------------------------------

function seedContext(sessionId: string): ClientContextSnapshot {
  const employer = seedPick(`${sessionId}:employer`, EMPLOYER_NAMES);
  const region   = seedPick(`${sessionId}:region`, REGIONS);
  const tenureMonths = 3 + (djb2(`${sessionId}:tenure`) % 48);
  const hhSize = 1 + (djb2(`${sessionId}:hhsize`) % 5);
  const needOptions = ['transportation', 'housing', 'childcare', 'debt'];
  const needCount = djb2(`${sessionId}:needcount`) % 3;
  const knownNeeds: string[] = [];
  for (let i = 0; i < needCount; i++) {
    const need = seedPick(`${sessionId}:need:${i}`, needOptions);
    if (!knownNeeds.includes(need)) knownNeeds.push(need);
  }
  return {
    employer,
    region,
    tenure: `${tenureMonths} months`,
    employmentType: djb2(`${sessionId}:emptype`) % 5 === 0 ? 'Part-time' : 'Full-time',
    householdSize: hhSize,
    knownNeeds,
    lastUpdatedAt: isoDate(2),
  };
}

function seedResources(sessionId: string, context: ClientContextSnapshot): RankedResource[] {
  const types: ResourceType[] = ['Employer', 'Employer', 'Community', 'Community', 'Community', 'Community', 'Other'];
  const resources: RankedResource[] = [];

  for (let i = 0; i < 20; i++) {
    const type = seedPick(`${sessionId}:type:${i}`, types);
    const names = RESOURCE_NAMES[type];
    const name = names[djb2(`${sessionId}:name:${i}`) % names.length];
    const cats = CATEGORIES_BY_TYPE[type];
    const category = cats[djb2(`${sessionId}:cat:${i}`) % cats.length];

    const eligibilityStatuses: EligibilityStatus[] = [
      'Eligible', 'Eligible', 'Potentially Eligible', 'Potentially Eligible',
      'Needs More Info', 'Not Eligible',
    ];
    const eligibilityStatus = seedPick(`${sessionId}:elig:${i}`, eligibilityStatuses);
    const eligibilityScore = eligibilityStatus === 'Eligible'
      ? 75 + (djb2(`${sessionId}:escore:${i}`) % 26)
      : eligibilityStatus === 'Potentially Eligible'
      ? 45 + (djb2(`${sessionId}:escore:${i}`) % 30)
      : eligibilityStatus === 'Needs More Info'
      ? 30 + (djb2(`${sessionId}:escore:${i}`) % 20)
      : 5 + (djb2(`${sessionId}:escore:${i}`) % 20);

    const rankScore = eligibilityScore - 5 + (djb2(`${sessionId}:rscore:${i}`) % 15);

    const metCount = 1 + (djb2(`${sessionId}:metc:${i}`) % 3);
    const missingCount = eligibilityStatus === 'Not Eligible' ? 1 + (djb2(`${sessionId}:missc:${i}`) % 2) : 0;
    const unknownCount = eligibilityStatus === 'Needs More Info' ? 1 + (djb2(`${sessionId}:unknc:${i}`) % 2) : 0;

    const criteriaMet = CRITERIA_MET_OPTIONS.slice(0, metCount);
    const criteriaMissing = CRITERIA_MISSING_OPTIONS.slice(0, missingCount);
    const criteriaUnknown = CRITERIA_UNKNOWN_OPTIONS.slice(0, unknownCount);

    const whyShown = seedPick(`${sessionId}:why:${i}`, WHY_SHOWN_TEMPLATES)
      .replace('{employer}', context.employer)
      .replace('{region}', context.region);

    const whyNotShown = eligibilityStatus === 'Not Eligible'
      ? seedPick(`${sessionId}:whynot:${i}`, WHY_NOT_SHOWN_TEMPLATES)
          .replace('{region}', context.region)
      : undefined;

    const confidenceLevels = ['high', 'high', 'medium', 'medium', 'low'] as const;
    const confidenceLevel = seedPick(`${sessionId}:conf:${i}`, confidenceLevels);

    const evCount = 1 + (djb2(`${sessionId}:evc:${i}`) % 3);
    const evidenceSources = EVIDENCE_SOURCES.slice(0, evCount);

    const rrCount = 1 + (djb2(`${sessionId}:rrc:${i}`) % 3);
    const rankReasonCodes = RANK_REASON_CODES.slice(0, rrCount);

    const hasPhone = djb2(`${sessionId}:phone:${i}`) % 3 !== 0;
    const hasEmail = djb2(`${sessionId}:email:${i}`) % 2 === 0;
    const hasWeb   = true;

    resources.push({
      resourceId: `res-${sessionId.slice(0, 6)}-${i}`,
      name,
      type,
      category,
      providerName: type === 'Employer' ? context.employer : `${context.region.split(',')[0]} Services`,
      coverageRegion: type === 'Employer' ? 'National' : context.region,
      contactPhone: hasPhone ? `(${600 + i}) 555-${1000 + i}` : undefined,
      contactEmail: hasEmail ? `contact@resource${i}.org` : undefined,
      websiteUrl: hasWeb ? `https://resources.example.org/${name.toLowerCase().replace(/\s+/g, '-')}` : undefined,
      tags: [category, type.toLowerCase(), ...rankReasonCodes.slice(0, 1)],
      description: `${name} provides targeted support for eligible clients in ${context.region}. Contact the provider for program details and enrollment.`,
      sourceSystem: seedPick(`${sessionId}:src:${i}`, ['Internal matrix', 'Employer policy', 'Findhelp feed', 'Regional DB']),
      lastVerifiedAt: isoDate(5 + (djb2(`${sessionId}:lv:${i}`) % 90)),
      // Eligibility
      eligibilityStatus,
      eligibilityScore,
      criteriaMet,
      criteriaMissing,
      criteriaUnknown,
      whyShownText: whyShown,
      whyNotShownText: whyNotShown,
      confidenceLevel,
      evidenceSources,
      // Rank
      rankScore,
      rankReasonCodes,
      // Actions
      savedByFA: false,
      contactedAt: undefined,
      contactMethod: undefined,
      outcomeLinked: undefined,
    });
  }

  // Sort by rankScore descending
  return resources.sort((a, b) => b.rankScore - a.rankScore);
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

interface SessionEntry {
  context: ClientContextSnapshot;
  resources: RankedResource[];
  filters: ResourceFilters;
  explorerPage: number;
}

@Injectable()
export class ClientResourcesStateService {
  private readonly _store = signal<Record<string, SessionEntry>>({});
  readonly storeSnapshot = this._store.asReadonly();

  ensureState(sessionId: string): void {
    if (this._store()[sessionId]) return;
    const context = seedContext(sessionId);
    const resources = seedResources(sessionId, context);
    this._store.update(s => ({
      ...s,
      [sessionId]: {
        context,
        resources,
        filters: { ...DEFAULT_RESOURCE_FILTERS },
        explorerPage: 0,
      },
    }));
  }

  getState(sessionId: string): ResourcesState | null {
    const entry = this._store()[sessionId];
    if (!entry) return null;
    return {
      context: entry.context,
      allResources: entry.resources,
      filters: entry.filters,
      explorerPage: entry.explorerPage,
      explorerPageSize: 10,
    };
  }

  setFilter<K extends keyof ResourceFilters>(
    sessionId: string,
    key: K,
    value: ResourceFilters[K]
  ): void {
    this._store.update(s => {
      const entry = s[sessionId];
      if (!entry) return s;
      return {
        ...s,
        [sessionId]: {
          ...entry,
          filters: { ...entry.filters, [key]: value },
          explorerPage: 0,
        },
      };
    });
  }

  setExplorerPage(sessionId: string, page: number): void {
    this._store.update(s => {
      const entry = s[sessionId];
      if (!entry) return s;
      return { ...s, [sessionId]: { ...entry, explorerPage: page } };
    });
  }

  toggleSaved(sessionId: string, resourceId: string): void {
    this._store.update(s => {
      const entry = s[sessionId];
      if (!entry) return s;
      return {
        ...s,
        [sessionId]: {
          ...entry,
          resources: entry.resources.map(r =>
            r.resourceId === resourceId ? { ...r, savedByFA: !r.savedByFA } : r
          ),
        },
      };
    });
  }

  recordContact(
    sessionId: string,
    resourceId: string,
    method: 'phone' | 'email' | 'web'
  ): void {
    this._store.update(s => {
      const entry = s[sessionId];
      if (!entry) return s;
      return {
        ...s,
        [sessionId]: {
          ...entry,
          resources: entry.resources.map(r =>
            r.resourceId === resourceId
              ? { ...r, contactedAt: new Date().toISOString(), contactMethod: method }
              : r
          ),
        },
      };
    });
  }

  // ---------------------------------------------------------------------------
  // Derived views (computed outside the service for OnPush compat)
  // ---------------------------------------------------------------------------

  filteredResources(sessionId: string): RankedResource[] {
    const entry = this._store()[sessionId];
    if (!entry) return [];
    return applyFilters(entry.resources, entry.filters);
  }

  topRecommended(sessionId: string, limit = 10): RankedResource[] {
    const entry = this._store()[sessionId];
    if (!entry) return [];
    // Top N by rankScore, regardless of active filters
    return [...entry.resources]
      .sort((a, b) => b.rankScore - a.rankScore)
      .slice(0, limit);
  }

  employerResources(sessionId: string): RankedResource[] {
    const entry = this._store()[sessionId];
    if (!entry) return [];
    return entry.resources.filter(r => r.type === 'Employer');
  }

  communityResources(sessionId: string): RankedResource[] {
    const entry = this._store()[sessionId];
    if (!entry) return [];
    return entry.resources.filter(r => r.type === 'Community');
  }
}
